/**
 * MifeLife v2 — Mentor application form (PUBLIC, 2-step).
 *
 * Step 1: Profile info (name, contact, specialties, bio).
 * Step 2: Payout + KYC info (IBAN, identity/tax no, commission acceptance).
 * Plus 3 bottom info cards (Pilatika-inspired) outside the form.
 *
 * Submits to POST /api/v2/mentor-applications/.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, CheckCircle2, Sparkles, Wallet, ArrowRight, ArrowLeft,
  Briefcase, Rocket, TrendingUp, Zap, Brain, Award, Globe, Clock, Star,
} from 'lucide-react';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { SEO } from '../../components/SEO';
import { apiRequest } from '../../lib/utils';

// Trilingual recruitment-landing copy (the application form below stays in TR).
const RCOPY = {
  tr: {
    metaTitle: 'Mentör Ol — MifeLife | Kurucu Kohort Başvurusu',
    metaDesc: 'MifeLife’da mentör ol. Küresel kitle, AI destekli eşleştirme, esnek takvim, gelir fırsatı ve kurucu mentör statüsü. Hemen başvur.',
    heroBadge: 'MENTÖR BAŞVURUSU',
    heroH1: 'Bilgini gelire, deneyimini etkiye dönüştür',
    heroSub: 'MifeLife, AI analiziyle doğru danışanı sana getirir. Sen sadece bildiğini paylaş — biz eşleştirme, takvim, ödeme ve görünürlüğü hallederiz.',
    heroCta: 'Hemen başvur',
    whoTitle: 'Kimleri arıyoruz?',
    whoSub: 'Alanında gerçek deneyime sahip, insanları ileri taşımayı seven uzmanlar.',
    roles: [
      { icon: Briefcase, t: 'Kariyer mentörleri', d: 'Kariyer geçişi, terfi ve iş arama stratejisinde yol gösterenler.' },
      { icon: Rocket, t: 'Girişimci kurucular', d: 'Sıfırdan ürün ve şirket kurmuş, ölçeklemiş deneyimli kurucular.' },
      { icon: TrendingUp, t: 'Finans uzmanları', d: 'Bütçe, nakit akışı, yatırım ve finansal planlama uzmanları.' },
      { icon: Zap, t: 'Verimlilik uzmanları', d: 'Odak, alışkanlık ve zaman yönetiminde sonuç üretenler.' },
      { icon: Brain, t: 'Psikologlar', d: 'Lisanslı ruh sağlığı ve gelişim uzmanları (bilgilendirme amaçlı).' },
      { icon: Award, t: 'Koçlar', d: 'Sertifikalı yaşam, yönetici ve performans koçları.' },
    ],
    benTitle: 'Neden MifeLife?',
    benSub: 'Mentörlüğü gelir getiren, esnek ve global bir işe dönüştür.',
    benefits: [
      { icon: Globe, t: 'Küresel kitle', d: 'TR / EN / DE arayüz ile dünya genelinden danışanlara ulaş.' },
      { icon: Sparkles, t: 'AI destekli eşleştirme', d: 'AI analizi, en uygun danışanı doğrudan sana yönlendirir.' },
      { icon: Clock, t: 'Esnek takvim', d: 'Müsait saatlerini sen belirle, tatil modunu sen yönet.' },
      { icon: Wallet, t: 'Gelir fırsatı', d: 'Her seanstan %80 kazanç; net tutar 7 gün içinde IBAN’ına.' },
      { icon: Star, t: 'Kurucu mentör statüsü', d: 'İlk kohortta yer al; listelerde öncelik ve “Kurucu Kohort” rozeti.' },
    ],
    applyBadge: 'Kurucu Kohort Başvurusu',
    applyTitle: 'Başvurunu tamamla',
    applySub: 'Her seanstan %80 kazanırsın, %20 platform komisyonudur. Değerlendirme 48 saat içinde sonuçlanır.',
    successTitle: 'Başvurun alındı.',
    successBtn: 'Ana sayfaya dön',
  },
  en: {
    metaTitle: 'Become a Mentor — MifeLife | Founding Cohort Application',
    metaDesc: 'Become a mentor on MifeLife. Global audience, AI-powered matching, flexible scheduling, revenue opportunity and founding mentor status. Apply now.',
    heroBadge: 'MENTOR APPLICATION',
    heroH1: 'Turn your knowledge into income, your experience into impact',
    heroSub: 'MifeLife brings the right mentee to you through AI analysis. You just share what you know — we handle matching, scheduling, payments and visibility.',
    heroCta: 'Apply now',
    whoTitle: 'Who we’re looking for',
    whoSub: 'Experts with real experience who love moving people forward.',
    roles: [
      { icon: Briefcase, t: 'Career mentors', d: 'Guides for career transitions, promotions and job-search strategy.' },
      { icon: Rocket, t: 'Startup founders', d: 'Experienced founders who built and scaled products and companies.' },
      { icon: TrendingUp, t: 'Finance experts', d: 'Experts in budgeting, cash flow, investing and financial planning.' },
      { icon: Zap, t: 'Productivity experts', d: 'People who deliver results in focus, habits and time management.' },
      { icon: Brain, t: 'Psychologists', d: 'Licensed mental-health and development experts (informational).' },
      { icon: Award, t: 'Coaches', d: 'Certified life, executive and performance coaches.' },
    ],
    benTitle: 'Why MifeLife?',
    benSub: 'Turn mentorship into a profitable, flexible and global practice.',
    benefits: [
      { icon: Globe, t: 'Global audience', d: 'Reach mentees worldwide via a TR / EN / DE interface.' },
      { icon: Sparkles, t: 'AI-powered matching', d: 'AI analysis routes the most relevant mentee directly to you.' },
      { icon: Clock, t: 'Flexible scheduling', d: 'Set your own available hours and manage your vacation mode.' },
      { icon: Wallet, t: 'Revenue opportunity', d: '80% of every session; net amount to your IBAN within 7 days.' },
      { icon: Star, t: 'Founding mentor status', d: 'Join the first cohort: listing priority and a “Founding Cohort” badge.' },
    ],
    applyBadge: 'Founding Cohort Application',
    applyTitle: 'Complete your application',
    applySub: 'You earn 80% of every session, 20% is the platform commission. Review is completed within 48 hours.',
    successTitle: 'Your application is received.',
    successBtn: 'Back to home',
  },
  de: {
    metaTitle: 'Mentor werden — MifeLife | Gründer-Kohorte Bewerbung',
    metaDesc: 'Werde Mentor bei MifeLife. Globales Publikum, KI-gestützte Vermittlung, flexible Planung, Einkommenschance und Gründer-Mentor-Status. Jetzt bewerben.',
    heroBadge: 'MENTOR-BEWERBUNG',
    heroH1: 'Verwandle dein Wissen in Einkommen, deine Erfahrung in Wirkung',
    heroSub: 'MifeLife bringt dir über KI-Analyse den passenden Mentee. Du teilst nur dein Wissen — wir kümmern uns um Vermittlung, Planung, Zahlung und Sichtbarkeit.',
    heroCta: 'Jetzt bewerben',
    whoTitle: 'Wen wir suchen',
    whoSub: 'Experten mit echter Erfahrung, die Menschen gerne voranbringen.',
    roles: [
      { icon: Briefcase, t: 'Karriere-Mentoren', d: 'Begleiter für Karrierewechsel, Beförderungen und Jobsuche-Strategie.' },
      { icon: Rocket, t: 'Startup-Gründer', d: 'Erfahrene Gründer, die Produkte und Unternehmen aufgebaut und skaliert haben.' },
      { icon: TrendingUp, t: 'Finanzexperten', d: 'Experten für Budgetierung, Cashflow, Investitionen und Finanzplanung.' },
      { icon: Zap, t: 'Produktivitätsexperten', d: 'Menschen mit Ergebnissen in Fokus, Gewohnheiten und Zeitmanagement.' },
      { icon: Brain, t: 'Psychologen', d: 'Lizenzierte Experten für psychische Gesundheit und Entwicklung (informativ).' },
      { icon: Award, t: 'Coaches', d: 'Zertifizierte Life-, Executive- und Performance-Coaches.' },
    ],
    benTitle: 'Warum MifeLife?',
    benSub: 'Mache Mentoring zu einer profitablen, flexiblen und globalen Tätigkeit.',
    benefits: [
      { icon: Globe, t: 'Globales Publikum', d: 'Erreiche Mentees weltweit über eine TR / EN / DE-Oberfläche.' },
      { icon: Sparkles, t: 'KI-gestützte Vermittlung', d: 'Die KI-Analyse leitet den passendsten Mentee direkt zu dir.' },
      { icon: Clock, t: 'Flexible Planung', d: 'Lege deine Verfügbarkeiten fest und verwalte deinen Urlaubsmodus.' },
      { icon: Wallet, t: 'Einkommenschance', d: '80 % pro Sitzung; Nettobetrag innerhalb von 7 Tagen auf dein IBAN.' },
      { icon: Star, t: 'Gründer-Mentor-Status', d: 'Sei in der ersten Kohorte: Listing-Priorität und „Gründer-Kohorte“-Abzeichen.' },
    ],
    applyBadge: 'Gründer-Kohorte Bewerbung',
    applyTitle: 'Bewerbung abschließen',
    applySub: 'Du verdienst 80 % pro Sitzung, 20 % ist die Plattformprovision. Die Prüfung erfolgt innerhalb von 48 Stunden.',
    successTitle: 'Deine Bewerbung ist eingegangen.',
    successBtn: 'Zurück zur Startseite',
  },
};

const SPECIALTIES = [
  'Kariyer', 'İşletme', 'Yazılım', 'Finans', 'Dijital Pazarlama',
  'Liderlik', 'Girişimcilik', 'Satış', 'UX/UI', 'Veri Bilimi',
  'Finans Danışmanı', 'Pazarlama Danışmanı', 'Satış Koçu', 'Operasyon Danışmanı',
  'Diğer',
];
const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];
const DURATIONS = [30, 45, 60];

// Loose TR IBAN check — must start with TR, 26 chars total, only alphanum.
const isValidIban = (v) => {
  if (!v) return false;
  const stripped = v.replace(/\s/g, '').toUpperCase();
  return /^TR\d{24}$/.test(stripped);
};

export default function V2MentorApplyPage() {
  const [lang, setLang] = useState('tr');
  const RT = RCOPY[lang] || RCOPY.tr;
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    // Step 1
    first_name: '', last_name: '', email: '', phone: '',
    linkedin_url: '', specialties: [], hourly_rate_try: 400,
    languages: ['tr'], bio_short: '', why_mifelife: '', photo_url: '',
    // Step 2
    iban: '', identity_or_tax_no: '', default_session_duration: 60,
    certificate_url: '', identity_doc_url: '', commission_accepted: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  const toggleArr = (key, val) => setF((s) => ({
    ...s,
    [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val],
  }));
  const set = (key, val) => setF((s) => ({ ...s, [key]: val }));

  const validateStep1 = () => {
    if (!f.first_name || !f.last_name || !f.email) return 'Ad, soyad ve e-posta zorunlu.';
    if (f.specialties.length === 0) return 'En az 1 uzmanlık seç.';
    if (f.languages.length === 0) return 'En az 1 dil seç.';
    if (f.bio_short.length < 20) return 'Kısa bio en az 20 karakter olmalı.';
    if (f.why_mifelife.length < 10) return '"Neden MifeLife" en az 10 karakter olmalı.';
    return null;
  };
  const validateStep2 = () => {
    if (f.iban && !isValidIban(f.iban)) return 'Geçerli bir TR IBAN gir (örn: TR12 3456 7890 1234 5678 9012 34).';
    if (!f.commission_accepted) return 'Platform komisyonu onayını işaretle.';
    return null;
  };

  const goStep2 = () => {
    setErr(null);
    const v = validateStep1();
    if (v) { setErr(v); return; }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const v = validateStep2();
    if (v) { setErr(v); return; }
    setBusy(true);
    try {
      const payload = { ...f, hourly_rate_try: parseInt(f.hourly_rate_try, 10) };
      payload.iban = f.iban ? f.iban.replace(/\s/g, '').toUpperCase() : null;
      // Strip empty strings — backend optional fields stay null.
      ['phone', 'linkedin_url', 'photo_url', 'iban', 'identity_or_tax_no',
        'certificate_url', 'identity_doc_url'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      const res = await apiRequest('/v2/mentor-applications/', { method: 'POST', body: JSON.stringify(payload) });
      setDone(res);
    } catch (e2) {
      setErr(e2?.message || 'Başvuru gönderilemedi.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell lang={lang} onLang={setLang}>
        <SEO title={RT.metaTitle} description={RT.metaDesc} path="/mentor-basvuru" />
        <div className="max-w-2xl mx-auto text-center py-16" data-testid="v2-mapply-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold tracking-tight mb-3">{RT.successTitle}</h1>
          <p className="text-base text-slate-400 max-w-md mx-auto leading-relaxed">{done.message || 'Kurucu kohort değerlendirmesi 48 saat içinde sonuçlanır.'}</p>
          <Link to="/" className="mt-8 inline-flex items-center h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">{RT.successBtn}</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell lang={lang} onLang={setLang}>
      <SEO title={RT.metaTitle} description={RT.metaDesc} path="/mentor-basvuru" />

      {/* Recruitment hero */}
      <div className="mb-16" data-testid="v2-mapply-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-5">
          <Sparkles className="w-3 h-3" /> {RT.heroBadge}
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] max-w-3xl">{RT.heroH1}</h1>
        <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">{RT.heroSub}</p>
        <a href="#basvuru" className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-mapply-hero-cta">{RT.heroCta} <ArrowRight className="w-4 h-4" /></a>
      </div>

      {/* Who we're looking for */}
      <section className="mb-16" data-testid="v2-mapply-who">
        <h2 className="text-2xl font-semibold tracking-tight mb-2">{RT.whoTitle}</h2>
        <p className="text-sm text-slate-400 mb-6">{RT.whoSub}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {RT.roles.map((r, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition" data-testid={`v2-mapply-role-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/30 text-violet-300 flex items-center justify-center mb-3"><r.icon className="w-5 h-5" /></div>
              <h3 className="text-base font-semibold mb-1.5">{r.t}</h3>
              <p className="text-[13px] text-slate-400 leading-relaxed">{r.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="mb-16" data-testid="v2-mapply-benefits-top">
        <h2 className="text-2xl font-semibold tracking-tight mb-2">{RT.benTitle}</h2>
        <p className="text-sm text-slate-400 mb-6">{RT.benSub}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {RT.benefits.map((b, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`v2-mapply-benefit-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/30 text-violet-300 flex items-center justify-center mb-3"><b.icon className="w-5 h-5" /></div>
              <h3 className="text-base font-semibold mb-1.5">{b.t}</h3>
              <p className="text-[13px] text-slate-400 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <div id="basvuru" className="max-w-2xl mx-auto scroll-mt-28 pt-8 border-t border-white/[0.06]">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-5">
            <Sparkles className="w-3 h-3" /> {RT.applyBadge}
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{RT.applyTitle}</h2>
          <p className="mt-3 text-base text-slate-400 max-w-2xl">{RT.applySub}</p>
        </div>

        <Stepper step={step} />

        {step === 1 ? (
          <Step1Form f={f} set={set} toggleArr={toggleArr} err={err} onNext={goStep2} />
        ) : (
          <Step2Form f={f} set={set} err={err} busy={busy} onBack={() => { setStep(1); setErr(null); }} onSubmit={submit} />
        )}
      </div>
    </Shell>
  );
}

// ============================================================================
// STEPPER
// ============================================================================
const Stepper = ({ step }) => (
  <div className="flex items-center gap-3 mb-8" data-testid="v2-mapply-stepper">
    <StepBubble n={1} active={step >= 1} label="Profil bilgileri" />
    <div className={`flex-1 h-px ${step >= 2 ? 'bg-violet-400/50' : 'bg-white/10'} transition`} />
    <StepBubble n={2} active={step >= 2} label="Ödeme & onay" />
  </div>
);

const StepBubble = ({ n, active, label }) => (
  <div className="flex items-center gap-2">
    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border ${active ? 'bg-violet-500 border-violet-400 text-white' : 'bg-white/[0.04] border-white/10 text-slate-500'}`}>{n}</span>
    <span className={`hidden sm:inline text-xs uppercase tracking-[0.18em] ${active ? 'text-white' : 'text-slate-500'}`}>{label}</span>
  </div>
);

// ============================================================================
// STEP 1
// ============================================================================
const Step1Form = ({ f, set, toggleArr, err, onNext }) => (
  <form onSubmit={(e) => { e.preventDefault(); onNext(); }} className="space-y-4" data-testid="v2-mapply-step1">
    <div className="grid sm:grid-cols-2 gap-3">
      <FormField label="İsim" testid="v2-mapply-firstname">
        <input required value={f.first_name} onChange={(e) => set('first_name', e.target.value)} className={INPUT_CLS} />
      </FormField>
      <FormField label="Soyisim" testid="v2-mapply-lastname">
        <input required value={f.last_name} onChange={(e) => set('last_name', e.target.value)} className={INPUT_CLS} />
      </FormField>
    </div>
    <FormField label="E-posta" testid="v2-mapply-email">
      <input required type="email" value={f.email} onChange={(e) => set('email', e.target.value)} className={INPUT_CLS} />
    </FormField>
    <div className="grid sm:grid-cols-2 gap-3">
      <FormField label="Telefon (opsiyonel)" testid="v2-mapply-phone">
        <input value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+90..." className={INPUT_CLS} />
      </FormField>
      <FormField label="LinkedIn URL (önerilen)" testid="v2-mapply-linkedin">
        <input value={f.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." className={INPUT_CLS} />
      </FormField>
    </div>

    <FormField label="Uzmanlık alanları (en az 1)" testid="v2-mapply-specialties">
      <div className="flex flex-wrap gap-1.5">
        {SPECIALTIES.map((s) => (
          <button type="button" key={s} onClick={() => toggleArr('specialties', s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${f.specialties.includes(s) ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'border-white/10 bg-white/[0.02] text-slate-300 hover:bg-white/[0.05]'}`}>
            {s}
          </button>
        ))}
      </div>
    </FormField>

    <FormField label="Diller (en az 1)" testid="v2-mapply-languages">
      <div className="flex gap-1.5">
        {LANGUAGES.map((l) => (
          <button type="button" key={l.code} onClick={() => toggleArr('languages', l.code)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${f.languages.includes(l.code) ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'border-white/10 bg-white/[0.02] text-slate-300'}`}>
            {l.label}
          </button>
        ))}
      </div>
    </FormField>

    <FormField label={`Kısa biyografi (${f.bio_short.length}/500)`} testid="v2-mapply-bio">
      <textarea required minLength={20} maxLength={500} rows={4} value={f.bio_short} onChange={(e) => set('bio_short', e.target.value)} className={TEXTAREA_CLS} placeholder="Kendinizden ve mentörlük yaklaşımınızdan bahsedin..." />
    </FormField>
    <FormField label={`Neden MifeLife? (${f.why_mifelife.length}/300)`} testid="v2-mapply-why">
      <textarea required minLength={10} maxLength={300} rows={3} value={f.why_mifelife} onChange={(e) => set('why_mifelife', e.target.value)} className={TEXTAREA_CLS} placeholder="Bu platforma neden katılmak istiyorsun?" />
    </FormField>
    <FormField label="Fotoğraf URL (opsiyonel)" testid="v2-mapply-photo">
      <input value={f.photo_url} onChange={(e) => set('photo_url', e.target.value)} placeholder="https://..." className={INPUT_CLS} />
    </FormField>

    {err && <div className="text-sm text-rose-300" data-testid="v2-mapply-err1">{err}</div>}

    <button type="submit" className="w-full h-12 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition flex items-center justify-center gap-2" data-testid="v2-mapply-next">
      Devam et — Ödeme bilgileri <ArrowRight className="w-4 h-4" />
    </button>
  </form>
);

// ============================================================================
// STEP 2
// ============================================================================
const Step2Form = ({ f, set, err, busy, onBack, onSubmit }) => {
  const gross = parseInt(f.hourly_rate_try, 10) || 0;
  const platform = Math.round(gross * 0.20);
  const mentorNet = gross - platform;
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="v2-mapply-step2">
      <FormField label="Saatlik ücret (₺)" testid="v2-mapply-rate">
        <input required type="number" min="100" max="10000" step="50" value={f.hourly_rate_try} onChange={(e) => set('hourly_rate_try', e.target.value)} className={INPUT_CLS} />
        <div className="text-[11px] text-slate-500 mt-1.5">Önerilen aralık: ₺300 – ₺2.000. Mentöre net: <span className="text-emerald-300">₺{mentorNet}</span> · Platform: ₺{platform}</div>
      </FormField>

      <FormField label="Varsayılan seans süresi" testid="v2-mapply-duration">
        <div className="flex gap-2">
          {DURATIONS.map((d) => (
            <button type="button" key={d} onClick={() => set('default_session_duration', d)}
              className={`flex-1 h-10 text-sm rounded-lg border transition ${f.default_session_duration === d ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'border-white/10 bg-white/[0.02] text-slate-300'}`}>
              {d} dk
            </button>
          ))}
        </div>
      </FormField>

      <FormField label="IBAN (TR ile başlamalı, 26 karakter)" testid="v2-mapply-iban">
        <input value={f.iban} onChange={(e) => set('iban', e.target.value.toUpperCase())} placeholder="TR12 3456 7890 1234 5678 9012 34" className={INPUT_CLS} maxLength={32} />
        <div className="text-[11px] text-slate-500 mt-1.5">Ödemeleriniz tamamlanan seanstan 7 gün içinde bu IBAN'a aktarılır.</div>
      </FormField>

      <FormField label="TC Kimlik / Vergi No" testid="v2-mapply-tcno">
        <input value={f.identity_or_tax_no} onChange={(e) => set('identity_or_tax_no', e.target.value)} placeholder="11111111111" className={INPUT_CLS} maxLength={20} />
        <div className="text-[11px] text-slate-500 mt-1.5">Türkiye'de TC kimlik, yurt dışında vergi no. Ödeme + fatura için zorunlu.</div>
      </FormField>

      <FormField label="Sertifika / belge URL (opsiyonel)" testid="v2-mapply-cert">
        <input value={f.certificate_url} onChange={(e) => set('certificate_url', e.target.value)} placeholder="https://drive.google.com/..." className={INPUT_CLS} />
        <div className="text-[11px] text-slate-500 mt-1.5">Google Drive / Dropbox public link. Dosya yükleme yakında.</div>
      </FormField>

      <FormField label="Kimlik belgesi URL (opsiyonel, KYC için)" testid="v2-mapply-iddoc">
        <input value={f.identity_doc_url} onChange={(e) => set('identity_doc_url', e.target.value)} placeholder="https://drive.google.com/..." className={INPUT_CLS} />
      </FormField>

      <label className="flex items-start gap-3 p-4 rounded-lg border border-white/10 bg-white/[0.02] cursor-pointer" data-testid="v2-mapply-commission-label">
        <input
          type="checkbox"
          checked={f.commission_accepted}
          onChange={(e) => set('commission_accepted', e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-violet-500"
          data-testid="v2-mapply-commission"
        />
        <span className="text-[13px] text-slate-300 leading-relaxed">
          MifeLife platform komisyonunu (<span className="text-white font-semibold">%20</span>) kabul ediyorum.
          Her seanstan <span className="text-emerald-300">%80</span> kazancım, seans tamamlandıktan sonra 7 gün içinde IBAN'ıma aktarılır.
        </span>
      </label>

      {err && <div className="text-sm text-rose-300" data-testid="v2-mapply-err2">{err}</div>}

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} className="h-12 px-5 rounded-lg border border-white/10 bg-white/[0.02] text-white text-sm font-medium hover:bg-white/[0.05] transition flex items-center gap-2" data-testid="v2-mapply-back">
          <ArrowLeft className="w-4 h-4" /> Geri
        </button>
        <button type="submit" disabled={busy} className="flex-1 h-12 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="v2-mapply-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Başvuruyu gönder
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// SHELL / FIELDS
// ============================================================================
const INPUT_CLS = 'w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition';
const TEXTAREA_CLS = 'w-full px-3 py-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition resize-none';

const FormField = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>
    {children}
  </label>
);

const Shell = ({ lang = 'tr', onLang, children }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-mapply-page">
    <V2Nav lang={lang} onLangChange={onLang} />
    <section className="pt-28 sm:pt-32 pb-20">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">{children}</div>
    </section>
    <V2Footer lang={lang} />
  </div>
);
